/** Element measurement for charts that must fit whatever column they are dropped into. */

import { useEffect, useRef, useState, type RefObject } from 'react';

/** Width used before the first measurement, so the first paint is not zero-width. */
const FALLBACK_WIDTH = 320;

/**
 * Tracks the content width of an element.
 *
 * @returns a ref to attach to the measured element and its current width in pixels.
 */
export function useMeasuredWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry !== undefined) setWidth(Math.max(1, Math.round(entry.contentRect.width)));
    });
    observer.observe(element);
    setWidth(Math.max(1, Math.round(element.getBoundingClientRect().width)));
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
